import { Modal, useModals } from 'almanac-ui/Modal'
import { InformalFeedbackRequestState } from 'api/clients/InformalFeedbackRequestsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Button } from 'blaze-ui/Button'
import { Calendar2 } from 'blaze-ui/icons'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useStore } from 'react-redux'
import { useAuthOrUnregisteredUser } from 'hooks/useAuthOrUnregisteredUser'
import { useQueryClient } from 'react-query'
import { useInformalFeedbackRequest } from 'hooks/queries/informalFeedbackRequests/useInformalFeedbackRequest'
import dayjs from 'dayjs'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import Styles from './ReviewSummaryModal.module.scss'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { RequesterCard } from './RequesterCard/RequesterCard'
import { ReviewerCard } from './ReviewersCard/ReviewerCard'
import { EndReviewModal } from './EndReviewModal/EndReviewModal'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useState } from 'react'
import { AddMoreReviewers } from './AddMoreReviewers/AddMoreReviewers'
import { InformalFeedbackRequestsService } from 'api/services/AlmanacFiles/InformalFeedbackRequestsService'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { Permission } from 'types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Heading } from 'blaze-ui/Heading/Heading'

interface Props {
  requestId: number
}

export const ReviewSummaryModal = observer(({ requestId }: Props) => {
  const queryClient = useQueryClient()
  const { designStore } = useRootStore()
  const { isRegistered } = useAuthOrUnregisteredUser()
  const store = useStore()
  const { data } = useInformalFeedbackRequest(requestId)
  const { informal_feedback_request: request } = data || {}
  const article = useCurrentArticleMeta()
  const { openModal } = useModals()
  const { closeModal } = useModals()
  const [addNewReviewers, setAddNewReviewers] = useState<boolean>(false)

  if (!request || !request.almanac_file) {
    return null
  }

  const handleEndsOnChange = async (value: string | null) => {
    if (!value) {
      return
    }
    const res = await InformalFeedbackRequestsService.update(
      store,
      request.id,
      request.almanac_file.id,
      { endsOn: value },
      queryClient,
    )
    if (res?.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Due date updated successfully')
    } else {
      showErrorMessageToast('Something went wrong, try again')
    }
  }

  const handleEndRequest = () => {
    openModal(EndReviewModal, { request })
  }

  const hasEditAccess = designStore?.file?.permissions.can_edit || article?.current_user_access === Permission.Edit
  const canEditRequest = hasEditAccess && request.state === InformalFeedbackRequestState.Open
  const reviewerFieldWidth = Math.max(...request.reviews.map((r) => r.reviewer.display_name?.length || 0))

  return (
    <Modal.Root
      onPressOutside={() => {
        !!addNewReviewers ? setAddNewReviewers(false) : closeModal()
      }}
    >
      <Modal.Header
        title="Review Summary"
        actions={
          <div className={Styles.actions}>
            {canEditRequest && (
              <DueDatePicker
                value={request.ends_on}
                onChange={handleEndsOnChange}
                minValue={dayjs().format('YYYY-MM-DD')}
                shape="squared"
                variant="secondary"
                size="sm"
                readOnly={!isRegistered}
                className={Styles.dueDatePicker}
                frontIcon={Calendar2}
                showClearDate={false}
              />
            )}
            {canEditRequest && isRegistered && (
              <>
                <Tooltip placement="top" label={'Any editor can end the review'}>
                  <div>
                    <Button variant="secondary" size="sm" onPress={handleEndRequest}>
                      End Review
                    </Button>
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        }
      />
      <div className={Styles.body}>
        <RequesterCard request={request} />

        <div className={Styles.reviewersContainer}>
          <div className={Styles.reviewersHeader}>
            <Heading level={4}>Invitees</Heading>
            {canEditRequest && (
              <AddMoreReviewers
                request={request}
                addNewReviewers={addNewReviewers}
                setAddNewReviewers={setAddNewReviewers}
              />
            )}
          </div>
          <div className={Styles.reviewers}>
            {request.reviews.map((review) => (
              <ReviewerCard
                key={review.id}
                review={review}
                request={request}
                reviewerFieldWidth={reviewerFieldWidth * 8}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal.Root>
  )
})
